// Import the createApi and fetchBaseQuery functions from the "@reduxjs/toolkit/query/react" library
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
// Import a custom helper function "formatPrice" from "../scripts/helpers/formatPrice"
import { formatPrice } from "../scripts/helpers/formatPrice";

// Get the base URL for the API from the environment variable "BASE_URL"
const BASE_URL = process.env.BASE_URL + "/api";

// Import dummy data from "../mock/data", including the "imageSrc" variable
import { mock, cartItems } from "../mock/data";
const { imageSrc } = mock;

const formatOptionalPrice = (value) => {
    if (value === null || value === undefined || value === "") {
        return null;
    }

    return formatPrice(Number(value));
};

// Get the CSRF token value from a <meta> tag in the document header
const token = document.querySelector('meta[name="csrf-token"]').content;
// Get the current URL origin (protocol, hostname, and port number) using the "window.location.origin" property
const url = window.location.origin;

// Export a new API slice using the createApi() function from "@reduxjs/toolkit/query/react"
export const apiSlice = createApi({
    // Set the "reducerPath" property to "api" as the default name for this API slice.
    // If you use more than one API slice, you must give each one a unique name.
    reducerPath: "api",

    // Set the "baseQuery" property to a new fetchBaseQuery() object, which is a wrapper around the fetch() function.
    // Pass in an options object with a "baseUrl" property set to the value of the "BASE_URL" constant.
    // Use the "prepareHeaders" option to add the CSRF token as a custom header to each HTTP request if it is available.
    // Also set the "content-type" header to "application/json" for consistency.
    baseQuery: fetchBaseQuery({
        baseUrl: BASE_URL,
        prepareHeaders: (headers) => {
            if (token) {
                headers.set("x-csrftoken", token);
                headers.set("content-type", "application/json");
            }
            return headers;
        },
    }),

    // Define an array of strings called "tagTypes" containing the different types of tags used in the app.
    tagTypes: ["Models", "Accesories", "merchandise", "Maintenance", "Cart"],

    // API endpoints
    endpoints: (builder) => ({
        // Query endpoint for fetching home page data
        getHomeData: builder.query({
            // Endpoint URL
            query: () => "/store/home_featured",

            // Transform the raw response data into desired format
            transformResponse: (response) => {
                // Create a new Map object to group series items by segment
                const seriesBySegment = new Map();

                // Create an array to store the final transformed series items
                const seriesItems = [];

                // Obtener vehiculos en stock
                const seriesIdsWithVehicles = new Set(
                    response.vehicles_stock.map(vehicle => vehicle.serie.id)
                );
                const filteredSeries = response.series.filter(serie =>
                    seriesIdsWithVehicles.has(serie.id)
                );
                                
                // Iterate over each series item in the response
                filteredSeries.forEach((serie) => {
                    // Get the segment name for the current series item
                    const segmentName = serie.segment.name;

                    // Check if the seriesBySegment map already has an entry for the current segment
                    if (seriesBySegment.has(segmentName)) {
                        // If it does, add the current series item to the existing array
                        seriesBySegment.get(segmentName).push(serie);
                    } else {
                        // If it doesn't, create a new array with the current series item
                        seriesBySegment.set(segmentName, [serie]);
                    }
                });

                // Iterate over each segment in the seriesBySegment map
                seriesBySegment.forEach((key, value) => {
                    // Create an object to represent the segment and its associated series items
                    seriesItems.push({
                        title: value,
                        boxes: key.map((seriesItem) => {
                            return {
                                // Set the box title to the name of the series item
                                title: seriesItem.name,
                                // Set the box image to the URL of the series item's image
                                image: {
                                    url: seriesItem.image,
                                },
                                // Set the target and URL for the box link
                                target: "",
                                url: `/modelos/?segment=${seriesItem.segment.name.toLowerCase()}&serie=${seriesItem.name.toLowerCase()}&version=all`,
                            };
                        }),
                    });
                });

                const normalizedData = {
                    // Properties for the banner section at the top of the page
                    BannerTitleButtonProps: {
                        // Set the desktop and mobile images for the banner
                        imageDesktop: imageSrc,
                        imageMobile: imageSrc,
                        // Set the title for the banner
                        title: "Bienvenidos <br> a Tienda Lexus",
                        // Set the buttons for the banner
                        buttons: [
                            {
                                config: {
                                    // Set the tag for the button
                                    tag: "button-link",
                                    // Set the text for the button
                                    text: "Encuentra tu Lexus",
                                    // Set the color for the button
                                    color: "black",
                                    // Set the URL for the button
                                    url: "/modelos",
                                },
                            },
                        ],
                        // Set the title for the version items
                        titleItems: "Versiones destacadas",
                        // Set the link for viewing available versions
                        linkConfig: {
                            tag: "link",
                            text: "Ver versiones disponibles",
                            color: "black-and-white",
                            hasIcon: true,
                            extraClass: "with-icon",
                            url: "/modelos",
                        },
                        // Set the version items for the banner
                        items: response.vehicles.map((item) => {
                            return {
                                // Set the ID for the version item
                                id: item.id,
                                // Set the title for the version item
                                title: item.version.name,
                                // Set the subtitle for the version item
                                subtitle: item.slogan,
                                // Set the price for the version item
                                price: formatPrice(item.price),
                                // Set the image for the version item
                                image: item.grid_image,
                                // Set the URL for the version item
                                url: `/modelos/${item.slug}`,
                                // Set the URL for the version item
                                green_tax: item.green_tax
                                    ? formatPrice(item.green_tax)
                                    : 0,
                                // Set the type of item to "vehicle"
                                type: "vehicle",
                            };
                        }),
                        // Set the extra class for the banner
                        extraClass:
                            "--with-margin-top --store-bottom-black-fade",
                    },

                    // Properties for the section with title and button below the banner
                    TitleAndButtonProps: {
                        // Set the title for the section
                        title: "Conoce las series Lexus",
                        // Set the button for the section
                        buttons: [
                            {
                                config: {
                                    // Set the text for the button
                                    text: "Comparar modelos",
                                    // Set the color for the button
                                    color: "black",
                                    // Set the URL for the button
                                    url: `${url}/es/comparador`,
                                },
                            },
                        ],
                    },

                    // Properties for the series grid section in the middle of the page
                    SeriesGridContainerProps: {
                        // Set the series items for the grid
                        items: seriesItems,
                    },

                    // Properties for the section with title and button at the bottom of the page
                    TitleAndButtonBottomProps: {
                        // Set the title for the section
                        title: "Servicios de mantención",
                        // Set the button for the section
                        buttons: [
                            {
                                config: {
                                    // Set the tag for the button
                                    tag: "button-link",
                                    // Set the text for the button
                                    text: "Ver servicios disponibles",
                                    // Set the color for the button
                                    color: "black",
                                    // Set the URL for the button
                                    url: "/mantenciones",
                                },
                            },
                        ],
                    },

                    BoxWithBackgroundContainerProps: {
                        // Set the background image for the section
                        image: `${window._env_.STATIC_HOST}/static/website/images/lexus-mantenciones.jpg`,
                        // Set an extra CSS class for the section
                        extraClass: "store-padding",
                        // Set the maintenance items for the section
                        items: response.maintenances.map((item) => {
                            console.log(item)
                            const hasDiscountPrice =
                                item.price_discount !== null &&
                                item.price_discount !== undefined &&
                                item.price_discount !== "";
                            const originalPrice = hasDiscountPrice
                                ? item.price_discount
                                : item.min_price;
                            const discountedPrice = hasDiscountPrice
                                ? item.min_price
                                : null;

                            return {
                                // Set the type of item to "maintenance"
                                type: "maintenance",
                                // Set the ID for the maintenance item
                                id: item.id,
                                // Set the title for the maintenance item (the name of the service)
                                title: item.version.name,
                                // Set the subtitle for the maintenance item
                                subtitle: "Servicio de mantención",
                                // Set the minimum price for the maintenance item
                                price: formatPrice(originalPrice),
                                price_discount: formatOptionalPrice(discountedPrice),
                                // Set the image for the maintenance item
                                image: item.image,
                                // Set the URL for the maintenance item
                                url: `/mantenciones/${item.slug}`,
                            };
                        }),
                    // Set the title and button for the section
                    titleAndButton: true,
                    },
                };

                return normalizedData;
            },
        }),

        // Define the getAllModels query endpoint using builder.query()
        getAllModels: builder.query({
            // Set the API endpoint URL
            query: () => "/store/vehicles",

            // Transform the raw response data into normalized data format
            transformResponse: (response, meta, arg) => {
                // Remake the item object data
                const normalizedData = response.map((item) => {
                    return {
                        // Set the type of item to "modelo"
                        type: "modelo",
                        // Set the ID of the model
                        id: item.id,

                        // Set the slug of the model
                        slug: item.slug,
                        // Set the URL of the model
                        url: item.slug,

                        // Set the title of the model (the name of the version)
                        title: item.version.name,
                        // Set the subtitle of the model (the slogan for the version)
                        subtitle: item.slogan,
                        // Set the price of the model
                        price: formatPrice(item.price),

                        // Set the green tax of the model
                        green_tax: item.green_tax
                            ? formatPrice(item.green_tax)
                            : 0,

                        // Set the image of the model (the grid image)
                        image: item.grid_image,

                        // Set the segment of the model (the name of the segment in lowercase)
                        segment: item.segment.name.toLowerCase(),
                        // Set the serie of the model (the name of the serie in lowercase)
                        serie: item.serie.name.toLowerCase(),
                        // Set the version of the model (an object with an ID and the name of the version in lowercase)
                        version: {
                            id: item.version.id,
                            name: item.version.name.toLowerCase(),
                        },
                        // Set the stock warning of the model
                        stock_warning: item.stock_warning,
                        // Set the stock of the model
                        stock: item.stock,
                        // Set the color of the model (an object with the name of the color in lowercase and the hexadecimal code)
                        color: {
                            name: item.color.name.toLowerCase(),
                            hexcode: item.color.hexcode,
                        },
                    };
                });

                // Return the normalized data
                return normalizedData;
            },
        }),

        // `getModelById` is a query builder to retrieve vehicle information by ID
        getModelById: builder.query({
            // Define the API endpoint to fetch vehicle details using the given ID
            query: (id) => `/store/vehicles/${id}`,

            // Transform the API response into a normalized data structure
            transformResponse: (response, meta, arg) => {
                const normalizedData = {
                    ...response,
                    type: "modelo",
                    id: response.id,
                    slug: response.slug,
                    title: response.version.name,
                    subtitle: response.slogan,
                    detailSubtitle: `${response.year}. Color ${response.color.name}`,
                    option_code: response.option_code,
                    price: formatPrice(response.price),
                    green_tax: response.green_tax
                        ? formatPrice(response.green_tax)
                        : 0,
                    description: {
                        items: response.features.map((feature) => {
                            return {
                                icon: feature.name,
                                smallText: feature.value,
                            };
                        }),
                    },
                    image: response.primary_image,
                    secondaryImage: response.secondary_image,
                    currency: response.currency.symbol,

                    itemType: response.segment.name,
                    itemModel: response.serie.name,
                    itemVersion: response.version.name,

                    itemBookPrice: formatPrice(response.booking_price),
                    isHighlighted: true,

                    // TODO: FALTA
                    extraInfo: {
                        cash: {
                            currency: "USD",
                            price: response.price,
                        },
                        financing: {
                            price: response.price,
                        },
                    }, // Esta separado porque puede tener un precio en dolares para pagarlo cash y otro en pesos chilenos para financiarlo
                    legalInfo: response.legal_information,
                    inputs: {
                        dropdown: {
                            dropdownItems: response.branches.map((branch) => {
                                return {
                                    id: branch.branch.dealer.id,
                                    label: branch.branch.dealer.name,
                                    dataValue: branch.branch.dealer.id,
                                    isSelected: false,
                                };
                            }),
                        },
                    },
                };

                return normalizedData;
            },
        }),

        // `getAllMerchandising` is a query builder to fetch all merchandising items
        getAllMerchandising: builder.query({
            // Define the API endpoint to fetch merchandising items
            query: () => "/store/merchandise_items",

            // Transform the API response into a normalized data structure
            transformResponse: (response, meta, arg) => {
                // Remake the item object data
                const normalizedData = response.map((item) => {
                    return {
                        type: "merchandise",
                        id: item.id,
                        slug: item.slug,
                        url: item.slug,

                        title: item.name,
                        // Uncomment and populate the subtitle property if needed
                        // subtitle: "dummy",
                        price: formatPrice(item.price),
                        price_discount: formatOptionalPrice(item.discount_amount),
                        dark_background: item.dark_background,

                        image: item.image, // If API response has 'grid_image', use it instead
                        // Assign category name as lowercase for consistency
                        category: item.category.name.toLowerCase(),
                    };
                });

                return normalizedData;
            },
        }),

        // `getMerchandisingItemById` is a query builder to fetch a specific merchandising item by ID
        getMerchandisingItemById: builder.query({
            // Define the API endpoint to fetch the merchandising item using the given ID
            query: (id) => `/store/merchandise_items/${id}/`,

            // Transform the API response into a normalized data structure
            transformResponse: (response, meta, arg) => {
                const normalizedData = {
                    ...response,
                    type: "merchandise",
                    id: response.id,
                    slug: response.slug,

                    title: response.name,
                    // Uncomment and populate the subtitle and detailSubtitle properties if needed
                    // subtitle: "dummy",
                    // detailSubtitle: "dummy",
                    price: response.price,

                    image: response.image,
                    secondaryImage: null,

                    // Assign the item type as the category name in lowercase
                    itemType: response.category.name.toLowerCase(),

                    description: {
                        type: "html",
                        title: "Descripción",
                        items: response.description,
                    },

                    // Store legal information related to the merchandising item
                    legalInfo: response.legal_information,

                    size: response.variations.map((variation) => {
                        return {
                            id: variation.id,
                            label: variation.name,
                            dataValue: variation.name,
                            isSelected: false,
                        };
                    }),
                };

                return normalizedData;
            },
        }),

        // `getAllAccesories` is a query builder to fetch all accessories
        getAllAccesories: builder.query({
            // Define the API endpoint to fetch accessories
            query: () => "/store/accessories",

            // Transform the API response into a normalized data structure
            transformResponse: (response, meta, arg) => {
                // Remake the item object data
                const normalizedData = response.map((item) => {
                    return {
                        type: "accessory",
                        id: item.id,
                        slug: item.slug,
                        url: item.slug,

                        title: item.name,
                        price: formatPrice(Number(item.price)),
                        price_discount: formatOptionalPrice(item.discount_amount),
                        image: item.detail_image,
                        dark_background: item.dark_background,

                        // Store additional accessory properties
                        segments: item.segments,
                        versions: item.versions,
                        series: item.series,
                    };
                });

                return normalizedData;
            },
        }),

        // `getAccessoryById` is a query builder to fetch a specific accessory by ID
        getAccessoryById: builder.query({
            // Define the API endpoint to fetch the accessory using the given ID
            query: (id) => `/store/accessories/${id}`,

            // Transform the API response into a normalized data structure
            transformResponse: (response, meta, arg) => {
                const normalizedData = {
                    ...response,
                    type: "accessory",
                    id: response.id,
                    slug: response.slug,

                    title: response.name,
                    // subtitle: "dummy",
                    // detailSubtitle: "dummy",
                    price: Number(response.price),

                    image: response.grid_image, // Deberia venir como primary image
                    secondaryImage: response.detail_image, // Deberia venir como secondary image

                    // TODO: Replace this hardcoded data with actual API data when available
                    description: {
                        type: "html",
                        title: "Descripción",
                        items: response.description,
                    },

                    // Store legal information related to the accessory
                    legalInfo: response.legal_information,

                    // Convert accessory versions into a format suitable for a dropdown
                    versions: response.versions.map((version) => {
                        return {
                            label: version.name,
                            dataValue: version.name,
                            isSelected: false,
                        };
                    }),

                    // Set the installation cost if available, or null if not
                    installationCost:
                        response.variations.length > 0
                            ? Number(response.variations[0].price)
                            : null,
                };

                return normalizedData;
            },
        }),

        // `getAllMaintenancePlans` is a query builder to fetch all maintenance plans
        getAllMaintenancePlans: builder.query({
            // Define the API endpoint to fetch maintenance plans
            query: () => "/store/maintenance_plans",

            // Transform the API response into a normalized data structure
            transformResponse: (response, meta, arg) => {
                // Map each item in the response to the required data structure
                const normalizedData = response.map((item) => {
                    const hasDiscountPrice =
                        item.price_discount !== null &&
                        item.price_discount !== undefined &&
                        item.price_discount !== "";
                    const originalPrice = hasDiscountPrice
                        ? item.price_discount
                        : item.min_price;
                    const discountedPrice = hasDiscountPrice
                        ? item.min_price
                        : null;

                    return {
                        type: "maintenance",
                        id: item.id,
                        slug: item.slug,
                        url: item.slug,

                        title: item.version.name,
                        subtitle: "Servicio de mantención",
                        price: formatPrice(originalPrice),
                        price_discount: formatOptionalPrice(discountedPrice),

                        image: item.image,

                        documents: item.document,

                        segment: item.segment.name.toLowerCase(),
                        serie: item.serie.name.toLowerCase(),
                        version: {
                            id: item.version.id,
                            name: item.version.name,
                        },
                    };
                });

                return normalizedData;
            },
        }),

        // `getMaintenancePlanById` is a query builder to fetch a specific maintenance plan by its ID
        getMaintenancePlanById: builder.query({
            // Define the API endpoint to fetch a maintenance plan by ID
            query: (id) => `/store/maintenance_plans/${id}`,

            // Transform the API response into a normalized data structure
            transformResponse: (response, meta, arg) => {
                const normalizedData = {
                    ...response,
                    type: "maintenance",
                    id: response.id,
                    slug: response.slug,

                    title: response.version.name,
                    subtitle: "Servicio de mantención",
                    // price: formatPrice(response.price),

                    image: response.image,
                    currency: "CLP",
                    // discount: "Obtén 10% comprando 2 unidades, 15% comprando 3 unidades o 20% por compras de 4 o más.", // ASK: el texto de donde lo saco?

                    itemType: response.segment.name,
                    itemModel: response.serie.name,
                    itemVersion: response.version.name,

                    options: response.options,

                    mantenciones: response.options.map((option, i) => {
                        return {
                            label: `Mantención de los ${formatPrice(
                                option.kilometers
                            )} km`,
                            dataValue: option.id,
                            isSelected: i === 0 ? true : false,
                        };
                    }),

                    legalInfo: response.legal_information,
                };

                return normalizedData;
            },
        }),

        // `getCart` is a query builder to fetch the user's cart data
        getCart: builder.query({
            // Define the API endpoint to fetch the cart
            query: () => "/store/cart",
            providesTags: ["Cart"],

            // Transform the API response into a normalized data structure
            transformResponse: (response, meta, arg) => {
                const normalizedData = {
                    items: response.items
                        .map((item) => {
                            return {
                                id: item.id,
                                product_id: item.product_id,
                                title: item.name,
                                type: item.type,
                                price: item.price,
                                quantity: item.quantity,
                                totalPrice: item.sub_total,
                                image: item.image,
                                extra_info: item.extra_info,
                            };
                        })
                        .sort((a, b) => a.title.localeCompare(b.title)),
                    total: formatPrice(Number(response.total)),
                    discount: {
                        percentage: response.discount_percentage,
                        price: response.items_discount,
                    }
                };

                return normalizedData;
            },
        }),

        // `getFinancingData` is a query builder to fetch the financing data
        getFinancingData: builder.query({
            // Define the API endpoint to fetch the financing data
            query: () => "/store/financing",

            // Transform the API response into a normalized data structure
            transformResponse: (response) => {
                const financePlans = response.content.filter(
                    (plan) => plan.type === "finance_plan"
                );

                const normalizedData = {
                    title:
                        response.content[0].type === "title_subtitle"
                            ? response.content[0].value.title
                            : "",
                    subtitle:
                        response.content[0].type === "title_subtitle"
                            ? response.content[0].value.subtitle
                            : "",

                    plans: financePlans.map((financePlan, i) => {
                        return {
                            photo: i % 2 === 0 ? "left" : "right",

                            items: [
                                {
                                    image: financePlan.value.image,
                                    title: financePlan.value.title,
                                    subtitle: financePlan.value.subtitle,
                                    description: financePlan.value.description,
                                    boxes: financePlan.value.highlights,

                                    accordion: {
                                        items: [
                                            {
                                                label: "Conoce las características de este plan",
                                                content:
                                                    financePlan.value.features,
                                                hasHtml: true,
                                            },
                                        ],
                                    },
                                    // features: financePlan.value.features
                                },
                            ],
                        };
                    }),
                };

                return normalizedData;
            },
        }),

        // `getInsuranceData` is a query builder to fetch the ayuda data
        getAyudaData: builder.query({
            // Define the API endpoint to fetch the help (ayuda) data
            query: () => "/store/ayuda",

            // Transform the API response into a normalized data structure
            transformResponse: (response) => {
                const normalizedData = {
                    title: "Ayuda",

                    accordion: {
                        items: response.map((item) => {
                            return {
                                label: item.title,
                                content: item.text,
                                hasHtml: true,
                            };
                        }),
                    },
                };

                return normalizedData;
            },
        }),

        // `getInsuranceData` is a query builder to fetch the insurance data
        getTerminosCondicionesData: builder.query({
            // Define the API endpoint to fetch the terminos y condiciones data
            query: () => "/store/terminos_y_condiciones",

            // Transform the API response into a normalized data structure
            transformResponse: (response) => {
                const normalizedData = {
                    title: "Terminos y condiciones",

                    accordion: {
                        items: response.map((item) => {
                            return {
                                label: item.title,
                                content: item.text,
                                hasHtml: true,
                            };
                        }),
                    },
                };

                return normalizedData;
            },
        }),

        // `getInsuranceData` is a query builder to fetch the insurance data
        getPoliticasDePrivacidadData: builder.query({
            // Define the API endpoint to fetch the politicas de privacidad data
            query: () => "/store/politicas_de_privacidad",

            // Transform the API response into a normalized data structure
            transformResponse: (response) => {
                const normalizedData = {
                    title: "Políticas de privacidad",

                    accordion: {
                        items: response.map((item) => {
                            return {
                                label: item.title,
                                content: item.text,
                                hasHtml: true,
                            };
                        }),
                    },
                };

                return normalizedData;
            },
        }),

        // `getInsuranceData` is a query builder to fetch the insurance data
        getAllBranches: builder.query({
            // Define the API endpoint to fetch the dealers data
            query: () => "/dealers/branches",

            // Transform the API response into a normalized data structure
            transformResponse: (response) => {
                const normalizedData = response.map((item, i) => {
                    return {
                        id: item.id,
                        name: item.name,
                        salesman: item.dealer.name,
                        address: item.address,
                        phone: item.phone_numbers,
                        open_hours: item.open_hours,
                    };
                });

                return normalizedData;
            },
        }),

        // `getLegales` is a query builder to fetch the legales by page
        getLegales: builder.query({
            // Define the API endpoint to fetch the dealers data
            query: () => "/store/legales",
        }),

        // `getLegales` is a query builder to fetch discounts
        getDiscounts: builder.query({
            // Define the API endpoint to fetch discounts data
            query: () => "/store/discounts",
            transformResponse: (response, meta, arg) => {
                // the .reduce method is used to group discounts by product type
                const normalizedData = response.reduce(
                    (acc, { product_type, ...rest }) => {
                        acc[product_type] = [
                            ...(acc[product_type] || []),
                            rest,
                        ];
                        return acc;
                    },
                    {}
                );

                // Return the normalized data
                return normalizedData;
            },
        }),

        // Mutations
        // `addProduct` is a mutation builder to add a product to the cart
        addProduct: builder.mutation({
            // Define the API endpoint to add a product to the cart
            query: (product) => {
                return {
                    url: `/store/cart/add/`,
                    method: "POST",
                    body: product,
                };
            },

            // Define the invalidation tags to refresh the cart query
            invalidatesTags: ["Cart"],
        }),

        // `editProduct` is a mutation builder to edit a product in the cart
        editProduct: builder.mutation({
            // Define the API endpoint to edit a product in the cart
            query: (product) => {
                return {
                    url: `/store/cart/edit/`,
                    method: "PUT",
                    body: product,
                };
            },

            // Define the invalidation tags to refresh the cart query
            invalidatesTags: ["Cart"],
        }),

        // `deleteProduct` is a mutation builder to delete a product from the cart
        deleteProduct: builder.mutation({
            // Define the API endpoint to delete a product from the cart
            query: (product) => {
                return {
                    url: `/store/cart/remove/`,
                    method: "DELETE",
                    body: product,
                };
            },

            // Define the invalidation tags to refresh the cart query
            invalidatesTags: ["Cart"],
        }),

        // `clearCart` is a mutation builder to clear the cart
        clearCart: builder.mutation({
            // Define the API endpoint to clear the cart
            query: () => {
                return {
                    url: `/store/cart/clear/`,
                    method: "POST",
                };
            },

            // Define the invalidation tags to refresh the cart query
            invalidatesTags: ["Cart"],
        }),

        // `checkoutCart` is a mutation builder to checkout the cart
        checkoutCart: builder.mutation({
            // Define the API endpoint to checkout the cart
            query: (data) => {
                return {
                    url: `/store/cart/checkout/`,
                    method: "POST",
                    body: data,
                };
            },
        }),

        // `bookVehicle` is a mutation builder to book a vehicle
        bookVehicle: builder.mutation({
            // Define the API endpoint to book a vehicle
            query: (data) => {
                return {
                    url: `/store/cart/book_vehicle/`,
                    method: "POST",
                    body: data,
                };
            },
        }),

        // `creditSimulation` is a mutation builder to simulate a credit
        creditSimulation: builder.mutation({
            query: (data) => {
                return {
                    url: `/store/simulate_credit/`,
                    method: "POST",
                    body: data,
                };
            },
        }),
    }),
});

// Export the generated API hooks
export const {
    useGetHomeDataQuery,
    useGetAllModelsQuery,
    useGetAllAccesoriesQuery,
    useGetAllMerchandisingQuery,
    useGetAllMaintenancePlansQuery,
    useGetModelByIdQuery,
    useGetAccessoryByIdQuery,
    useGetMerchandisingItemByIdQuery,
    useGetMaintenancePlanByIdQuery,
    useGetCartQuery,
    useGetFinancingDataQuery,
    useGetAyudaDataQuery,
    useGetAllBranchesQuery,
    useGetPoliticasDePrivacidadDataQuery,
    useGetTerminosCondicionesDataQuery,
    useGetLegalesQuery,
    useGetDiscountsQuery,
    useAddProductMutation,
    useEditProductMutation,
    useDeleteProductMutation,
    useCheckoutCartMutation,
    useBookVehicleMutation,
    useCreditSimulationMutation,
} = apiSlice;
